import cn from 'classnames'
import { memo } from 'react'

import { IAccessibility } from 'clickNgo/models/widget'
import { FormCheckBox, FormDropdown, FormInput, FormMultiselect } from 'cng-common/features/form'
import { ValidationType } from 'cng-common/features/form/configs'
import { FieldGroup } from 'shared/components/fieldGroup'

import { ContactFormContent } from '../../config/forms'
import { TContactFormKeys } from '../../model/forms'
import {
  IContactAdditionalFormHandlers,
  IContactAdditionalFormRequiredFields,
  IContactAdditionalFormValidation,
} from '../../model/forms/additional'
import { IContact } from '../../model/types'
import { IContactSettingWithIcon } from '../../model/utils'

import {
  contactChangeMindsetSource,
  contactRelationshipSource,
  contactRolesSource,
  contactStatusSource,
} from './configs'
import { getCompetitorSource } from './helpers'

import styles from './ContactAdditionalInfo.module.scss'

interface IProps
  extends IAccessibility,
    IContactAdditionalFormHandlers,
    IContactAdditionalFormValidation,
    IContactAdditionalFormRequiredFields {
  contact: IContact
  formKeys: TContactFormKeys
  isCollapsible?: boolean
  isCollapsed?: boolean
  validationType?: ValidationType
}

const renderContactSettingWithIcon = (item: IContactSettingWithIcon) => (
  <>
    <i className={cn(item.class)} style={{ color: item.color, ...styles }} />
    <span>{item.hint}</span>
  </>
)

export const ContactAdditionalInfo = memo<IProps>((props) => {
  const competitorSource = getCompetitorSource()

  return (
    <FieldGroup
      title={ContactFormContent.titles.additional}
      size={3}
      isCollapsible={props.isCollapsible}
      isCollapsed={props.isCollapsed}
    >
      <FormInput
        field={ContactFormContent.fields.linkedInLink.field}
        label={ContactFormContent.fields.linkedInLink.label}
        formKey={props.formKeys.additional}
        defaultValue={props.contact.linkedInLink}
        onChange={props.onLinkedInLinkChange}
        onValidate={props.onLinkedInLinkValidate}
        isRequired={props.isLinkedInLinkRequired}
        isDisabled={props.isDisabled || props.isDisabledByRegion}
        isReadOnly={props.isReadOnly}
        validationType={props.validationType}
      />
      <FormInput
        field={ContactFormContent.fields.facebookLink.field}
        label={ContactFormContent.fields.facebookLink.label}
        formKey={props.formKeys.additional}
        defaultValue={props.contact.facebookLink}
        onChange={props.onFacebookLinkChange}
        onValidate={props.onFacebookLinkValidate}
        isRequired={props.isFacebookLinkRequired}
        isDisabled={props.isDisabled || props.isDisabledByRegion}
        isReadOnly={props.isReadOnly}
        validationType={props.validationType}
      />
      <FormInput
        field={ContactFormContent.fields.twitterLink.field}
        label={ContactFormContent.fields.twitterLink.label}
        formKey={props.formKeys.additional}
        defaultValue={props.contact.twitterLink}
        onChange={props.onTwitterLinkChange}
        onValidate={props.onTwitterLinkValidate}
        isRequired={props.isTwitterLinkRequired}
        isDisabled={props.isDisabled || props.isDisabledByRegion}
        isReadOnly={props.isReadOnly}
        validationType={props.validationType}
      />
      <FormInput
        field={ContactFormContent.fields.whatsAppLink.field}
        label={ContactFormContent.fields.whatsAppLink.label}
        formKey={props.formKeys.additional}
        defaultValue={props.contact.whatsAppLink}
        onChange={props.onWhatsAppLinkChange}
        onValidate={props.onWhatsAppLinkValidate}
        isRequired={props.isWhatsAppLinkRequired}
        isDisabled={props.isDisabled || props.isDisabledByRegion}
        isReadOnly={props.isReadOnly}
        validationType={props.validationType}
      />
      <div className={styles.separator} />
      <FormDropdown
        classes={{ controlClasses: { root: styles.newLine } }}
        formKey={props.formKeys.additional}
        defaultValue={props.contact.relationshipStrength}
        field={ContactFormContent.fields.relationshipStrength.field}
        label={ContactFormContent.fields.relationshipStrength.label}
        source={contactRelationshipSource}
        caption="hint"
        renderFunction={renderContactSettingWithIcon}
        onChange={props.onRelationshipStrengthChange}
        onValidate={props.onRelationshipStrengthValidate}
        isRequired={props.isRelationshipStrengthRequired}
        isStatic
        isDisabled={props.isDisabled || props.isDisabledByRegion}
        isReadOnly={props.isReadOnly}
        validationType={props.validationType}
      />
      <FormDropdown
        formKey={props.formKeys.additional}
        defaultValue={props.contact.changeMindset}
        field={ContactFormContent.fields.changeMindset.field}
        label={ContactFormContent.fields.changeMindset.label}
        source={contactChangeMindsetSource}
        caption="hint"
        renderFunction={renderContactSettingWithIcon}
        onChange={props.onChangeMindsetChange}
        onValidate={props.onChangeMindsetValidate}
        isRequired={props.isChangeMindsetRequired}
        isStatic
        isDisabled={props.isDisabled || props.isDisabledByRegion}
        isReadOnly={props.isReadOnly}
        validationType={props.validationType}
      />
      <FormDropdown
        formKey={props.formKeys.additional}
        defaultValue={props.contact.role}
        field={ContactFormContent.fields.role.field}
        label={ContactFormContent.fields.role.label}
        source={contactRolesSource}
        caption="hint"
        renderFunction={renderContactSettingWithIcon}
        onChange={props.onRoleChange}
        onValidate={props.onRoleValidate}
        isRequired={props.isRoleRequired}
        isStatic
        isDisabled={props.isDisabled || props.isDisabledByRegion}
        isReadOnly={props.isReadOnly}
        validationType={props.validationType}
      />
      <FormDropdown
        formKey={props.formKeys.additional}
        defaultValue={props.contact.contactStatus}
        field={ContactFormContent.fields.contactStatus.field}
        label={ContactFormContent.fields.contactStatus.label}
        source={contactStatusSource}
        caption="hint"
        renderFunction={renderContactSettingWithIcon}
        onChange={props.onContactStatusChange}
        onValidate={props.onContactStatusValidate}
        isRequired={props.isContactStatusRequired}
        isStatic
        isDisabled={props.isDisabled || props.isDisabledByRegion}
        isReadOnly={props.isReadOnly}
        validationType={props.validationType}
      />
      <FormMultiselect
        formKey={props.formKeys.additional}
        defaultValue={props.contact.competitors}
        onChange={props.onCompetitorsChange}
        onValidate={props.onCompetitorsValidate}
        isRequired={props.isCompetitorsRequired}
        field={ContactFormContent.fields.competitors.field}
        label={ContactFormContent.fields.competitors.label}
        source={competitorSource}
        caption="value"
        isDisabled={props.isDisabled || props.isDisabledByRegion}
        isReadOnly={props.isReadOnly}
        validationType={props.validationType}
        isStatic
      />
      <FormCheckBox
        className={styles.newLine}
        defaultValue={props.contact.isPreferredContact}
        field={ContactFormContent.fields.isPreferredContact.field}
        label={ContactFormContent.fields.isPreferredContact.label}
        formKey={props.formKeys.additional}
        onChange={props.onIsPreferredContactChange}
        onValidate={props.onIsPreferredContactValidate}
        isRequired={props.isPreferredContactRequired}
        isDisabled={props.isDisabled || props.isDisabledByRegion}
        validationType={props.validationType}
        isReadOnly={props.isReadOnly}
      />
      <FormCheckBox
        className={styles.newLine}
        defaultValue={props.contact.isHPHero}
        field={ContactFormContent.fields.isHPHero.field}
        label={ContactFormContent.fields.isHPHero.label}
        formKey={props.formKeys.additional}
        onChange={props.onIsHPHeroChange}
        onValidate={props.onIsHPHeroValidate}
        isRequired={props.isHPHeroRequired}
        isDisabled={props.isDisabled || props.isDisabledByRegion}
        validationType={props.validationType}
        isReadOnly={props.isReadOnly}
      />
    </FieldGroup>
  )
})

ContactAdditionalInfo.displayName = 'ContactAdditionalInfo'
